import React from 'react';
import { OffthreadVideo, useRemotionEnvironment } from 'remotion';
import type { OffthreadVideoProps } from 'remotion';
import { Video } from '@remotion/media';
import type { VideoProps } from '@remotion/media';
import { VideoMedia } from './types';
import { CanvasRendererForVideo } from './CanvasRendererForVideo';
import { MediaRole, normalizeMediaRole } from '../MediaRole';
import { EffectType } from '../effects';
import { CropBox } from '../CropBox';
import { ObjectFit } from '../types/style';
import {
    SerializableState,
    VideoMediaState,
    Serializable
} from '../types/serializable';
import { validateMediaState } from '../schemas/timeline-schema';
import { deserializeEffects, serializeEffects } from '../effects/serialization';

export class VideoMediaImpl implements VideoMedia, Serializable {
    id: string;
    type: 'video';
    role: MediaRole;
    src: string;
    offset?: number;
    duration?: number;
    effects?: EffectType[];
    crop?: CropBox;
    transparencyMode?: 'none' | 'alpha' | 'screen';
    muted?: boolean;
    volume?: number;
    usePartialDownload?: boolean;

    constructor(video: Omit<VideoMedia, 'render'>) {
        this.id = video.id;
        this.type = video.type;
        this.role = video.role;
        this.src = video.src;
        this.offset = video.offset;
        this.duration = video.duration;
        this.effects = video.effects;
        this.crop = video.crop;
        this.transparencyMode = video.transparencyMode;
        this.muted = video.muted;
        this.volume = video.volume;
        this.usePartialDownload = video.usePartialDownload;

        // 🔍 DEBUG: Verify src is being set (development only)
        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMediaImpl] Constructor called:', {
                id: this.id,
                src: this.src,
                hasSrc: !!this.src,
                srcLength: this.src?.length || 0,
                inputSrc: video.src
            });
        }

        if (!this.src) {
            // Avoid logging entire video object in production - only include safe identifiers
            const safeInfo = `id: ${video.id}, src length: ${video.src?.length || 0}`;
            throw new TypeError(`[VideoMediaImpl] Constructor received undefined src. ${safeInfo}`);
        }
    }

    /**
     * Export VideoMedia to serializable state
     */
    exportState(): VideoMediaState {
        const state: VideoMediaState = {
            type: 'VideoMedia',
            version: '1.0.0',
            id: this.id,
            src: this.src
        };

        // Add optional properties
        if (this.role !== undefined) state.role = this.role;
        if (this.offset !== undefined) state.offset = this.offset;
        if (this.duration !== undefined) state.duration = this.duration;
        if (this.volume !== undefined) state.volume = this.volume;
        if (this.muted !== undefined) state.muted = this.muted;
        if (this.transparencyMode !== undefined) state.transparencyMode = this.transparencyMode;
        if (this.effects !== undefined && this.effects.length > 0) {
            state.effects = serializeEffects(this.effects);
        }
        if (this.crop !== undefined) state.crop = this.crop;
        if (this.usePartialDownload !== undefined) state.usePartialDownload = this.usePartialDownload;

        return state;
    }

    /**
     * Import VideoMedia from serialized state
     */
    static importState(state: SerializableState): VideoMediaImpl {
        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMediaImpl] importState called with:', {
                stateType: state.type,
                hasState: !!state,
                stateKeys: Object.keys(state)
            });
        }

        const validatedState = validateMediaState(state);
        if (validatedState.type !== 'VideoMedia') {
            throw new Error(`Expected VideoMedia state, received ${validatedState.type}`);
        }

        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMediaImpl] Validated state:', {
                id: validatedState.id,
                src: validatedState.src,
                hasSrc: !!validatedState.src,
                srcLength: validatedState.src?.length || 0
            });
        }

        if (!validatedState.src) {
            throw new Error(`[VideoMediaImpl] Missing required src property for video media (id: ${validatedState.id}, type: ${validatedState.type})`);
        }

        return new VideoMediaImpl({
            id: validatedState.id,
            type: 'video',
            role: normalizeMediaRole(validatedState.role),
            src: validatedState.src,
            offset: validatedState.offset,
            duration: validatedState.duration,
            volume: validatedState.volume,
            muted: validatedState.muted,
            transparencyMode: validatedState.transparencyMode,
            effects: deserializeEffects(validatedState.effects),
            crop: validatedState.crop,
            usePartialDownload: validatedState.usePartialDownload
        });
    }

    render(fps: number, blockId: string, duration: number, objectFit?: ObjectFit, videoConfig?: { width: number; height: number }): React.ReactNode {
        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMedia] render() called:', {
                id: this.id,
                src: this.src,
                offset: this.offset,
                duration: this.duration,
                effects: this.effects?.length || 0,
                transparencyMode: this.transparencyMode,
                objectFit: objectFit,
                usePartialDownload: this.usePartialDownload
            });
        }

        if (!videoConfig) {
            throw new Error('VideoMediaImpl.render requires videoConfig parameter');
        }
        const { width, height } = videoConfig;

        // Choose video component based on usePartialDownload flag AND rendering environment
        // Per Remotion docs: https://www.remotion.dev/docs/player/player-methods#using-a-different-tag-in-preview-and-rendering
        // Video (@remotion/media): Use during RENDERING (Lambda) for HTTP range requests (90% bandwidth savings)
        // OffthreadVideo (remotion): Use during PREVIEW (browser) for better codec support

        const { isRendering } = useRemotionEnvironment();

        // HYBRID APPROACH:
        // - Use Video ONLY when usePartialDownload=true AND isRendering=true (Lambda with HTTP range requests)
        // - Otherwise use OffthreadVideo (browser preview OR when partial download disabled)
        // This avoids BroadcastChannel errors while still enabling bandwidth optimization when possible
        const VideoComponent = (this.usePartialDownload && isRendering) ? Video : OffthreadVideo;

        // 🔍 DEBUG: Log video offset calculation
        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMedia] Video offset calculation:', {
                id: this.id,
                offsetSeconds: this.offset,
                offsetType: typeof this.offset,
                fps: fps,
                calculatedStartFrame: this.offset ? this.offset * fps : 0,
                roundedStartFrame: this.offset ? Math.round(this.offset * fps) : 0,
                actualStartSeconds: this.offset ? Math.round(this.offset * fps) / fps : 0,
                differenceFromOriginal: this.offset ? (Math.round(this.offset * fps) / fps) - this.offset : 0
            });
        }

        // Prepare video props based on component type
        // Video (@remotion/media): uses trimBefore/trimAfter
        // OffthreadVideo (remotion): uses startFrom/endAt

        const trimStartFrame = this.offset && this.offset > 0 ? Math.floor(this.offset * fps) : undefined;
        const trimEndFrame = this.duration ? Math.ceil((this.offset ?? 0) * fps + this.duration * fps) : undefined;

        // Debug video trimming (development only)
        if (process.env.NODE_ENV === 'development' && this.offset && this.offset > 0) {
            console.log(`[VIDEO TRIM] ${this.id}: offset=${this.offset}s → trim=frame ${trimStartFrame}`);
        }

        // Common props for both Video and OffthreadVideo
        const commonVideoProps = {
            src: this.src,
            muted: this.muted ?? false,
            volume: this.volume ?? 1,
            delayRenderTimeoutInMilliseconds: 30000,
            delayRenderRetries: 3,
            onError: (error: Error) => {
                console.error(`[VideoMedia] Loading error for ${this.id}:`, error);
                console.error(`[VideoMedia] Video details:`, {
                    src: this.src,
                    offset: this.offset,
                    duration: this.duration
                });
            }
        };

        const shouldUsePartialDownloadVideo = VideoComponent === Video;

        const videoStyle: React.CSSProperties = {
            width: '100%',
            height: '100%'
        };

        // Handle transparency based on mode
        if (this.transparencyMode === 'alpha') {
            // WebM with alpha channel - use Remotion's transparent prop where supported.
            videoStyle.opacity = 0; // Canvas renderer handles opacity for alpha videos
        } else if (this.transparencyMode === 'screen') {
            // MP4 with black background - use screen blend mode to remove black
            videoStyle.mixBlendMode = 'screen';
            videoStyle.opacity = 1; // Full opacity needed for screen blend mode to work
        } else {
            // Regular video - full opacity for normal playback
            videoStyle.opacity = 1;
        }

        const renderVideoElement = (style: React.CSSProperties) => {
            const key = `${blockId}-${this.id}`;

            if (shouldUsePartialDownloadVideo) {
                const mediaVideoProps: VideoProps = {
                    ...commonVideoProps,
                    ...(trimStartFrame !== undefined && { trimBefore: trimStartFrame }),
                    ...(trimEndFrame !== undefined && { trimAfter: trimEndFrame }),
                    ...(this.transparencyMode === 'alpha' && {
                        fallbackOffthreadVideoProps: { transparent: true }
                    }),
                    style
                };

                return (
                    <Video
                        key={key}
                        {...mediaVideoProps}
                    />
                );
            }

            const offthreadVideoProps: OffthreadVideoProps = {
                ...commonVideoProps,
                ...(trimStartFrame !== undefined && { startFrom: trimStartFrame }),
                ...(trimEndFrame !== undefined && { endAt: trimEndFrame }),
                ...(this.transparencyMode === 'alpha' && { transparent: true }),
                pauseWhenBuffering: true,
                style
            };

            return (
                <OffthreadVideo
                    key={key}
                    {...offthreadVideoProps}
                />
            );
        };

        // Bypass CanvasRendererForVideo only for:
        // 1. Regular videos without effects AND no alpha transparency (no canvas overhead needed)
        // NOTE: Alpha videos MUST use CanvasRenderer to handle transparency properly
        // NOTE: Videos with effects MUST use CanvasRenderer to apply those effects (even with screen blend mode)
        const shouldBypassCanvas =
            (this.transparencyMode !== 'alpha' && (!this.effects || this.effects.length === 0));
        if (process.env.NODE_ENV === 'development') {
            console.log('[VideoMedia] Rendering path decision:', {
                shouldBypassCanvas,
                transparencyMode: this.transparencyMode,
                hasEffects: !!this.effects && this.effects.length > 0
            });
        }

        if (shouldBypassCanvas) {
            // Apply objectFit directly to the video style for fullscreen rendering
            const finalVideoStyle: React.CSSProperties = {
                objectFit: objectFit || 'cover', // Default to cover for fullscreen
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 1 // Default to fully opaque
            };

            // Apply transparency mode styling
            // NOTE: Alpha videos never reach this path - they use CanvasRenderer
            if (this.transparencyMode === 'screen') {
                // MP4 with black background - use screen blend to remove black
                finalVideoStyle.mixBlendMode = 'screen';
                finalVideoStyle.filter = 'contrast(1.1) brightness(1.05)';
                finalVideoStyle.opacity = 1;
            }
            // else: transparencyMode === 'none' or undefined - use default (fully opaque, no blend mode)

            // Debug logging in development
            if (process.env.NODE_ENV === 'development') {
                console.log(`[SCREEN BLEND DEBUG] ${this.id}:`, {
                    src: this.src,
                    transparencyMode: this.transparencyMode,
                    mixBlendMode: finalVideoStyle.mixBlendMode,
                    objectFit: finalVideoStyle.objectFit
                });
            }

            return (
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    // Ensure the container doesn't interfere with blending
                    isolation: 'auto'
                }}>
                    {renderVideoElement(finalVideoStyle)}
                </div>
            );
        }

        // For all other cases, use CanvasRendererForVideo for effects support
        return (
            <CanvasRendererForVideo
                blockId={blockId}
                mediaId={this.id}
                width={width}
                height={height}
                effects={this.effects}
                objectFit={objectFit}
            >
                {renderVideoElement(videoStyle)}
            </CanvasRendererForVideo>
        );
    }
}
